import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, extractErrorCode } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { Check, Loader2, Sparkles, X, Scale } from "lucide-react";
import { toast } from "sonner";
import { Fable5WaitlistBanner } from "@/components/Fable5WaitlistBanner";
import { LegalProComparisonTable } from "@/components/LegalProComparisonTable";

const PLAN_FEATURES = {
  free: ["30 chat messages/month", "5 images/month", "3 code projects/month", "2 PDF documents Q&A", "Single model", "Community support"],
  starter: ["500 chat messages/month", "30 images/month", "20 code projects/month", "10 PDF documents Q&A", "Multi-model: Claude, GPT, Gemini", "Email support"],
  pro: ["3,000 chat messages/month", "200 images/month", "100 code projects/month", "50 PDF documents Q&A", "Multi-model: Claude, GPT, Gemini", "Priority support", "Full history retained"],
  business: ["15,000 chat messages/month", "1,000 images/month", "500 code projects/month", "200 PDF documents Q&A", "Multi-model: Claude, GPT, Gemini", "Priority support", "Full history retained", "API access"],
};

export default function Pricing() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loadingId, setLoadingId] = useState(null);
  const [activePromo, setActivePromo] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [validating, setValidating] = useState(false);
  const [plansData, setPlansData] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly"); // 'monthly' | 'annual'

  // Load active promo + plan prices on mount
  useImperativeLoad(() => {
    api.get("/payments/plans").then((r) => {
      setPlansData(r.data);
      if (r.data?.active_promo) {
        setActivePromo(r.data.active_promo);
      }
    });
  }, []);

  // Auto-fill promo from URL ?promo=
  useImperativeLoad(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("promo");
    if (code) setPromoInput(code.toUpperCase());
  }, []);

  // Promo codes are not valid for annual — auto-clear if user switches.
  const handleCycleChange = (cycle) => {
    setBillingCycle(cycle);
    if (cycle === "annual" && appliedPromo) {
      setAppliedPromo(null);
      setPromoInput("");
      toast.info(t("pricing.promo_cleared_annual"));
    }
  };

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    if (billingCycle === "annual") {
      toast.error(t("pricing.err_PROMO_NOT_VALID_FOR_ANNUAL"));
      return;
    }
    setValidating(true);
    try {
      const r = await api.get(
        `/payments/promo/${encodeURIComponent(promoInput.trim().toUpperCase())}?plan_id=pro`
      );
      if (r.data?.valid) {
        setAppliedPromo(r.data);
        toast.success(t("pricing.promo_applied", { price: r.data.discounted_price_eur }));
      } else {
        const code = r.data?.error_code || "PROMO_INVALID_CODE";
        const translated = t(`pricing.err_${code}`);
        toast.error(typeof translated === "string" && !translated.startsWith("pricing.") ? translated : `Error: ${code}`);
        setAppliedPromo(null);
      }
    } catch (e) {
      toast.error("Validation failed");
    } finally {
      setValidating(false);
    }
  };

  const clearPromo = () => {
    setAppliedPromo(null);
    setPromoInput("");
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      const refQuery = appliedPromo ? `&promo=${appliedPromo.code}` : "";
      navigate(`/auth?mode=signup&next=/pricing${refQuery}`);
      return;
    }
    if (planId === "free") {
      navigate("/dashboard");
      return;
    }
    setLoadingId(planId);
    try {
      const body = {
        plan_id: planId,
        provider: "stripe",
        origin_url: window.location.origin,
        billing_cycle: billingCycle,
        ...(billingCycle === "monthly" && appliedPromo && appliedPromo.plan_id === planId
          ? { promo_code: appliedPromo.code }
          : {}),
      };
      const res = await api.post("/payments/checkout", body);
      if (res.data?.url) {
        window.location.assign(res.data.url);
      }
    } catch (err) {
      const errCode = extractErrorCode(err);
      const translated = t(`pricing.err_${errCode}`);
      toast.error(typeof translated === "string" && !translated.startsWith("pricing.") ? translated : `Payment error: ${errCode}`);
    } finally {
      setLoadingId(null);
    }
  };

  // Build the displayed plan list from backend data (fallback to static prices)
  const apiPlans = Array.isArray(plansData?.plans) ? plansData.plans : [];
  const findApi = (id) => apiPlans.find((p) => p.id === id);

  const PLANS = [
    { id: "free", free: true, monthlyPrice: 0, annualPrice: 0, savingsEur: 0, monthsFree: 0 },
    { id: "starter", monthlyPrice: findApi("starter")?.price_eur ?? 29, annualPrice: findApi("starter")?.annual_price_eur ?? 290, savingsEur: findApi("starter")?.annual_savings_eur ?? 58, monthsFree: findApi("starter")?.annual_months_free ?? 2 },
    { id: "pro", monthlyPrice: findApi("pro")?.price_eur ?? 79, annualPrice: findApi("pro")?.annual_price_eur ?? 790, savingsEur: findApi("pro")?.annual_savings_eur ?? 158, monthsFree: findApi("pro")?.annual_months_free ?? 2, popular: true },
    { id: "business", monthlyPrice: findApi("business")?.price_eur ?? 199, annualPrice: findApi("business")?.annual_price_eur ?? 1990, savingsEur: findApi("business")?.annual_savings_eur ?? 398, monthsFree: findApi("business")?.annual_months_free ?? 2 },
  ];

  const legalPlan = {
    id: "legal_pro",
    monthlyPrice: findApi("legal_pro")?.price_eur ?? 149,
    annualPrice: findApi("legal_pro")?.annual_price_eur ?? 1490,
    savingsEur: findApi("legal_pro")?.annual_savings_eur ?? 298,
  };
  const legalDisplayPrice =
    billingCycle === "annual"
      ? (legalPlan.annualPrice / 12).toFixed(2)
      : legalPlan.monthlyPrice;
  const isLegalCurrent = user?.subscription_tier === "legal_pro";

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="pricing-page">
      <Seo
        title={t("seo.pricing_title")}
        description={t("seo.pricing_description")}
        canonicalPath="/pricing"
        ogImage="/brand/og-default.png"
      />
      <Header />

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        {activePromo && billingCycle === "monthly" && (
          <div
            className="mb-10 overflow-hidden rounded-2xl bg-gradient-to-r from-[#05073B] to-[#1A1D7A] p-6 shadow-lg"
            data-testid="promo-banner"
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#9ACD32]">
                  <Sparkles className="h-5 w-5 text-[#05073B]" />
                </div>
                <div>
                  <p className="font-display text-xl font-bold text-white">
                    {t("pricing.promo_banner_title")}
                  </p>
                  <p className="mt-1 text-sm text-slate-300">
                    {t("pricing.promo_banner_subtitle", {
                      code: activePromo.code,
                      remaining: activePromo.remaining ?? "?",
                    })}
                  </p>
                </div>
              </div>
              <code
                className="rounded-lg border border-[#9ACD32]/40 bg-[#9ACD32]/10 px-4 py-2 font-mono text-base font-bold text-[#9ACD32]"
                data-testid="promo-banner-code"
              >
                {activePromo.code}
              </code>
            </div>
          </div>
        )}

        <div className="mb-10 text-center">
          <h1 className="font-display text-5xl font-extrabold tracking-tight text-[#05073B] sm:text-6xl">
            {t("pricing.title")}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
            {t("pricing.subtitle")}
          </p>
        </div>

        {/* Billing cycle toggle */}
        <div
          className="mx-auto mb-10 flex w-fit items-center gap-1 rounded-full border border-slate-300 bg-white p-1 shadow-sm"
          data-testid="billing-cycle-toggle"
        >
          <button
            type="button"
            onClick={() => handleCycleChange("monthly")}
            className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${
              billingCycle === "monthly"
                ? "bg-[#0B0E5C] text-white"
                : "text-slate-600 hover:text-[#05073B]"
            }`}
            data-testid="billing-cycle-monthly"
          >
            {t("pricing.cycle_monthly")}
          </button>
          <button
            type="button"
            onClick={() => handleCycleChange("annual")}
            className={`relative rounded-full px-5 py-2 text-sm font-bold transition-colors ${
              billingCycle === "annual"
                ? "bg-[#0B0E5C] text-white"
                : "text-slate-600 hover:text-[#05073B]"
            }`}
            data-testid="billing-cycle-annual"
          >
            {t("pricing.cycle_annual")}
            <span
              className="ml-2 inline-flex items-center rounded-full bg-[#9ACD32] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#05073B]"
              data-testid="billing-cycle-save-badge"
            >
              {t("pricing.cycle_save_badge")}
            </span>
          </button>
        </div>

        {/* Promo code input — only meaningful for monthly billing */}
        {billingCycle === "monthly" && (
          <div className="mx-auto mb-10 max-w-xl">
            <Card className="border-slate-200 bg-white p-4">
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                {t("pricing.promo_label")}
              </label>
              <div className="mt-2 flex gap-2">
                <Input
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  placeholder={t("pricing.promo_placeholder")}
                  maxLength={20}
                  className="h-11 flex-1 rounded-lg font-mono uppercase"
                  disabled={!!appliedPromo}
                  data-testid="promo-input"
                />
                {appliedPromo ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={clearPromo}
                    className="h-11 rounded-lg"
                    data-testid="promo-clear-btn"
                  >
                    <X className="mr-1 h-4 w-4" />
                    {t("pricing.promo_clear")}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleApplyPromo}
                    disabled={!promoInput.trim() || validating}
                    className="h-11 rounded-lg bg-[#0B0E5C] text-white hover:bg-[#1A1D7A]"
                    data-testid="promo-apply-btn"
                  >
                    {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : t("pricing.promo_apply")}
                  </Button>
                )}
              </div>
              {appliedPromo && (
                <p className="mt-2 text-xs font-semibold text-[#7AAA20]" data-testid="promo-applied-msg">
                  {t("pricing.promo_applied", { price: appliedPromo.discounted_price_eur })}
                </p>
              )}
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((plan) => {
            const isCurrent = user?.subscription_tier === plan.id;
            const promoApplies =
              billingCycle === "monthly" &&
              appliedPromo &&
              appliedPromo.plan_id === plan.id;

            // Monthly display: either promo-discounted or regular monthly price.
            // Annual display: total annual / 12, with the full annual cost in subtitle.
            let displayedPrice;
            let priceSuffix;
            let secondaryNote = null;
            if (plan.free) {
              displayedPrice = 0;
              priceSuffix = t("pricing.per_month");
            } else if (billingCycle === "annual") {
              displayedPrice = (plan.annualPrice / 12).toFixed(2);
              priceSuffix = t("pricing.per_month");
              secondaryNote = t("pricing.annual_total", {
                total: plan.annualPrice,
                saved: plan.savingsEur,
              });
            } else if (promoApplies) {
              displayedPrice = appliedPromo.discounted_price_eur;
              priceSuffix = t("pricing.per_month");
            } else {
              displayedPrice = plan.monthlyPrice;
              priceSuffix = t("pricing.per_month");
            }

            return (
              <Card
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative flex flex-col p-8 transition-all ${
                  plan.popular
                    ? "border-2 border-[#9ACD32] bg-[#05073B] text-white shadow-xl"
                    : "border-slate-200 bg-white"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="rounded-full bg-[#9ACD32] px-4 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#05073B]">
                      {t("pricing.most_popular")}
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3
                    className={`font-display text-2xl font-extrabold ${
                      plan.popular ? "text-white" : "text-[#05073B]"
                    }`}
                  >
                    {t(`pricing.${plan.id}_name`)}
                  </h3>
                  <p
                    className={`mt-1 text-sm ${
                      plan.popular ? "text-slate-300" : "text-slate-500"
                    }`}
                  >
                    {t(`pricing.${plan.id}_tagline`)}
                  </p>
                </div>

                <div className="mb-8">
                  <div className="flex items-baseline gap-2">
                    {(promoApplies || billingCycle === "annual") && !plan.free && (
                      <span
                        className={`text-2xl line-through ${plan.popular ? "text-slate-500" : "text-slate-400"}`}
                        data-testid={`plan-price-original-${plan.id}`}
                      >
                        €{plan.monthlyPrice}
                      </span>
                    )}
                    <span
                      className={`font-display text-5xl font-extrabold ${
                        plan.popular ? "text-white" : "text-[#05073B]"
                      } ${promoApplies || billingCycle === "annual" ? "text-[#9ACD32]" : ""}`}
                      data-testid={`plan-price-${plan.id}`}
                    >
                      €{displayedPrice}
                    </span>
                    <span
                      className={`text-sm ${
                        plan.popular ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      {priceSuffix}
                    </span>
                  </div>
                  {promoApplies && (
                    <p className="mt-1 text-xs font-semibold text-[#9ACD32]">
                      {t("pricing.promo_first_month_note", { price: plan.monthlyPrice })}
                    </p>
                  )}
                  {secondaryNote && (
                    <p
                      className={`mt-1 text-xs font-semibold ${plan.popular ? "text-[#9ACD32]" : "text-[#7AAA20]"}`}
                      data-testid={`plan-annual-note-${plan.id}`}
                    >
                      {secondaryNote}
                    </p>
                  )}
                </div>

                <ul
                  className={`mb-8 flex-1 space-y-3 text-sm ${
                    plan.popular ? "text-slate-200" : "text-slate-700"
                  }`}
                >
                  {(PLAN_FEATURES[plan.id] || []).map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check
                        className={`mt-0.5 h-4 w-4 shrink-0 ${
                          plan.popular ? "text-[#9ACD32]" : "text-[#7AAA20]"
                        }`}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loadingId === plan.id || isCurrent}
                  data-testid={`plan-cta-${plan.id}`}
                  className={`h-12 w-full rounded-full text-sm font-bold ${
                    plan.popular
                      ? "lime-glow bg-[#9ACD32] text-[#05073B] hover:bg-[#A8DF38]"
                      : plan.free
                      ? "border-[#0B0E5C] bg-white text-[#0B0E5C] hover:bg-slate-50"
                      : "bg-[#0B0E5C] text-white hover:bg-[#1A1D7A]"
                  }`}
                  variant={plan.free && !plan.popular ? "outline" : "default"}
                >
                  {loadingId === plan.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isCurrent ? (
                    t("pricing.current_plan")
                  ) : plan.free ? (
                    t("pricing.cta_free")
                  ) : (
                    t("pricing.cta_paid")
                  )}
                </Button>
              </Card>
            );
          })}
        </div>

        {/* Legal Pro — specialized vertical hero card */}
        <div className="mt-16" data-testid="legal-pro-section">
          <div className="mx-auto mb-6 text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#D4A937]/30 bg-[#D4A937]/10 px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#A67C0F]">
              <Scale className="h-3 w-3" />
              {t("pricing.legal_specialized")}
            </span>
          </div>
          <Card
            className="relative overflow-hidden border-2 border-[#D4A937]/40 bg-gradient-to-br from-[#05073B] via-[#0F1149] to-[#1A0F40] p-8 text-white shadow-2xl lg:p-12"
            data-testid="plan-card-legal_pro"
          >
            {/* Decorative scales-of-justice background icon */}
            <Scale className="absolute -right-8 -top-8 h-64 w-64 text-[#D4A937]/5" />

            <div className="relative grid items-center gap-8 lg:grid-cols-2">
              <div>
                <h2 className="font-display text-3xl font-extrabold lg:text-4xl">
                  {t("pricing.legal_pro_name")}
                </h2>
                <p className="mt-3 max-w-md text-sm text-slate-300 lg:text-base">
                  {t("pricing.legal_pro_tagline")}
                </p>

                <ul className="mt-6 grid gap-2.5 text-sm">
                  {[
                    "legal_pro_feature_quota",
                    "legal_pro_feature_opus",
                    "legal_pro_feature_default_on",
                    "legal_pro_feature_kb",
                    "legal_pro_feature_templates",
                    "legal_pro_feature_rag",
                    "legal_pro_feature_priority",
                  ].map((key) => (
                    <li key={key} className="flex items-start gap-2.5">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#F4C955]" />
                      <span className="text-slate-200">{t(`pricing.${key}`)}</span>
                    </li>
                  ))}
                </ul>
                <Fable5WaitlistBanner />
              </div>

              <div className="flex flex-col items-start lg:items-end">
                <div className="flex items-baseline gap-2">
                  {billingCycle === "annual" && (
                    <span className="text-2xl text-slate-500 line-through" data-testid="plan-price-original-legal_pro">
                      €{legalPlan.monthlyPrice}
                    </span>
                  )}
                  <span className="font-display text-6xl font-extrabold text-[#F4C955]" data-testid="plan-price-legal_pro">
                    €{legalDisplayPrice}
                  </span>
                  <span className="text-sm text-slate-400">{t("pricing.per_month")}</span>
                </div>
                {billingCycle === "annual" && (
                  <p className="mt-1 text-xs font-semibold text-[#F4C955]" data-testid="plan-annual-note-legal_pro">
                    {t("pricing.annual_total", {
                      total: legalPlan.annualPrice,
                      saved: legalPlan.savingsEur,
                    })}
                  </p>
                )}

                <Button
                  onClick={() => handleSubscribe("legal_pro")}
                  disabled={loadingId === "legal_pro" || isLegalCurrent}
                  data-testid="plan-cta-legal_pro"
                  className="mt-6 h-13 w-full max-w-xs rounded-full bg-[#F4C955] py-3.5 text-sm font-extrabold uppercase tracking-wider text-[#05073B] shadow-[0_8px_32px_rgba(244,201,85,0.35)] transition-all hover:bg-[#FFD96A] hover:shadow-[0_12px_40px_rgba(244,201,85,0.5)]"
                >
                  {loadingId === "legal_pro" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isLegalCurrent ? (
                    t("pricing.current_plan")
                  ) : (
                    <>
                      <Scale className="mr-2 h-4 w-4" />
                      {t("pricing.legal_pro_cta")}
                    </>
                  )}
                </Button>

                <p className="mt-3 max-w-xs text-[11px] leading-relaxed text-slate-400 lg:text-right">
                  {t("pricing.legal_pro_target")}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <LegalProComparisonTable />

      <Footer />
    </div>
  );
}
